import _ from "lodash";
import {
    Directive,
    OnDestroy,
    OnChanges,
    ElementRef,
    HostListener,
    Input,
    SimpleChanges,
    inject
} from "@angular/core";

import { LgTranslateService } from "@logex/framework/lg-localization";
import {
    ITooltipOptions,
    LgTooltipService,
    TooltipApi,
    TooltipPosition
} from "@logex/framework/ui-core";

@Directive({
    selector: "[tooltipObserved]",
    host: {
        "[class.lg-tooltip-visible]": "_visible"
    },
    standalone: false
})
export class TooltipObservedDirective implements OnDestroy, OnChanges {
    private _elementRef = inject(ElementRef);
    private _tooltipService = inject(LgTooltipService);
    private _translateService = inject(LgTranslateService);
    @Input("tooltipClass") tooltipClass: string;

    @Input("tooltipPosition") tooltipPosition: TooltipPosition;

    @Input("tooltipOptions") tooltipOptions: ITooltipOptions;

    @Input("tooltipParams") parameters: any;

    @Input("tooltipInterpolation") tooltipInterpolation: string;

    @Input("tooltipObserved") content: string | (() => string);

    public _visible = false;
    private _api: TooltipApi;
    private _observer: IntersectionObserver;

    public constructor() {
        this._api = this._tooltipService.create({
            content: () => this._getContent(),
            target: this._elementRef,
            tooltipClass: "lg-tooltip lg-tooltip--simple"
        });

        this._observer = new IntersectionObserver(entries => {
            // todo: keep x and y from boundingClientRect in order to evaluate the change of position
            if (entries.length === 1) {
                if (entries[0].isIntersecting) this._api.reposition();
            } else if (entries.findIndex(x => x.isIntersecting) >= 0) {
                if (this.tooltipOptions?.stay || this.tooltipOptions?.waitForMouse)
                    this._api.reposition();
                else this._api.hide();
            }
        });
        this._observer.observe(this._elementRef.nativeElement);
        this._api.afterVisibilityChanged().subscribe(visible => (this._visible = visible));
    }

    @HostListener("mouseenter")
    public onMouseEnter(): void {
        this._api.scheduleShow();
    }

    @HostListener("mouseleave")
    public onMouseLeave(): void {
        this._api.scheduleHide();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.tooltipClass) {
            this._api.options({
                tooltipClass: changes.tooltipClass.currentValue || "lg-tooltip lg-tooltip--simple"
            });
        }
        if (changes.tooltipPosition) {
            this._api.options({ position: changes.tooltipPosition.currentValue || "bottom-left" });
        }
        if (changes.tooltipOptions && changes.tooltipOptions.currentValue) {
            this._api.options(changes.tooltipOptions.currentValue);
        }
    }

    public ngOnDestroy(): void {
        this._observer.unobserve(this._elementRef.nativeElement);
        this._api.destroy();
    }

    private _getContent(): string {
        let content: string;
        if (_.isFunction(this.content)) {
            content = this.content();
        } else if (this.content[0] === "=") {
            content = this.content.substring(1);
        } else {
            // todo: interpolation
            content = this._translateService.translate(this.content, this.parameters);
        }
        return content.replace(/\n/g, "<br />");
    }
}
